<script>
  import { onMount } from 'svelte';
  import ItemCard from './ItemCard.svelte';
  import ItemModal from './ItemModal.svelte';
  import { 
    inventory, 
    fetchInventory, 
    isLoading, 
    inventoryError,
    availableItems 
  } from '../../services/inventoryService.js';
  import { t } from '../../services/i18n.js';
  import { trackSearch, trackFilter } from '../../services/analyticsService.js';
  import { CATEGORIES, CATEGORY_LABELS } from '../../utils/constants.js';

  let searchQuery = $state('');
  let selectedCategory = $state(CATEGORIES.ALL);
  let selectedItem = $state(null);
  let showModal = $state(false);

  // Debug logging
  $effect(() => {
    console.log('[ItemCatalog] Total items in inventory:', $inventory.items?.length);
    console.log('[ItemCatalog] Available items:', $availableItems?.length);
    $availableItems?.forEach((item, index) => {
      console.log(`  [${index}] SKU: ${item.id}, Name: ${item.name}, Available: ${item.available}, Category: ${item.category}`);
    });
  });

  // Filtered items based on search and category
  let filteredItems = $derived.by(() => {
    let items = $availableItems;
    
    console.log('[ItemCatalog] Starting filter - availableItems count:', items?.length);

    // Filter by category
    if (selectedCategory !== CATEGORIES.ALL) {
      const beforeFilter = items.length;
      items = items.filter(item => 
        item.category.toLowerCase() === selectedCategory.toLowerCase()
      );
      console.log(`[ItemCatalog] Category filter (${selectedCategory}): ${beforeFilter} -> ${items.length}`);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const beforeFilter = items.length;
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
      console.log(`[ItemCatalog] Search filter (${query}): ${beforeFilter} -> ${items.length}`);
    }

    console.log('[ItemCatalog] Final filtered items:', items.length);
    items.forEach((item, index) => {
      console.log(`  [${index}] Will render: ${item.id} - ${item.name}`);
    });

    return items;
  });

  function handleSearch(e) {
    searchQuery = e.target.value;
    if (searchQuery.length >= 3) {
      trackSearch(searchQuery);
    }
  }

  function handleCategoryChange(category) {
    selectedCategory = category;
    trackFilter('category', category);
  }

  function handleItemClick(item) {
    selectedItem = item;
    showModal = true;
  }

  function handleCloseModal() {
    showModal = false;
    selectedItem = null;
  }

  onMount(() => {
    fetchInventory();
  });

  // Dynamic categories based on available items
  const categories = $derived.by(() => {
    const uniqueCategories = new Set();
    $availableItems.forEach(item => {
      if (item.category) {
        uniqueCategories.add(item.category);
      }
    });
    
    // Always include "Todos" first
    const result = [['todos', 'Todos']];
    
    // Add categories that exist in inventory
    const categoryMap = {
      'roupas': 'Roupas',
      'acessorios': 'Acessórios',
      'calcados': 'Calçados',
      'bolsas': 'Bolsas',
      'outros': 'Outros'
    };
    
    // Sort with custom order: roupas first, outros last
    const sortedCategories = Array.from(uniqueCategories).sort((a, b) => {
      if (a === 'roupas') return -1;
      if (b === 'roupas') return 1;
      if (a === 'outros') return 1;
      if (b === 'outros') return -1;
      return a.localeCompare(b);
    });
    
    sortedCategories.forEach(cat => {
      result.push([cat, categoryMap[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)]);
    });
    
    return result;
  });
</script>

<section class="catalog" aria-labelledby="catalog-title">
  <header class="catalog-header">
    <h1 id="catalog-title" class="catalog-title">{$t('catalog.title')}</h1>
    <p class="catalog-subtitle">{$t('catalog.subtitle')}</p>
  </header>

  <!-- Search and Filters -->
  <div class="catalog-controls">
    <div class="search-box">
      <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        type="search"
        class="search-input"
        placeholder={$t('catalog.search')}
        value={searchQuery}
        oninput={handleSearch}
        aria-label={$t('catalog.search')}
      />
    </div>

    <nav class="category-filters" aria-label="Filtrar por categoria">
      {#each categories as [key, label]}
        <button
          class="category-btn"
          class:active={selectedCategory === key}
          onclick={() => handleCategoryChange(key)}
          aria-pressed={selectedCategory === key}
        >
          {label}
        </button>
      {/each}
    </nav>
  </div>

  <!-- Loading State -->
  {#if $isLoading}
    <div class="catalog-loading" role="status" aria-live="polite">
      <div class="loading-grid">
        {#each Array(6) as _}
          <div class="skeleton-card">
            <div class="skeleton skeleton-image"></div>
            <div class="skeleton-content">
              <div class="skeleton skeleton-title"></div>
              <div class="skeleton skeleton-price"></div>
            </div>
          </div>
        {/each}
      </div>
      <span class="sr-only">{$t('catalog.loading')}</span>
    </div>

  <!-- Error State -->
  {:else if $inventoryError}
    <div class="catalog-error" role="alert">
      <div class="error-icon">😿</div>
      <p>{$t('catalog.error')}</p>
      <p class="error-message">{$inventoryError}</p>
      <button class="btn btn-primary" onclick={fetchInventory}>
        {$t('catalog.retry')}
      </button>
    </div>

  <!-- Empty State -->
  {:else if filteredItems.length === 0}
    <div class="catalog-empty">
      <div class="empty-icon">📦</div>
      <p>{$t('catalog.empty')}</p>
      {#if searchQuery || selectedCategory !== CATEGORIES.ALL}
        <button 
          class="btn btn-secondary"
          onclick={() => { searchQuery = ''; selectedCategory = CATEGORIES.ALL; }}
        >
          Limpar filtros
        </button>
      {/if}
    </div>

  <!-- Items Grid -->
  {:else}
    <div class="items-grid">
      {#each filteredItems as item (item.id)}
        <ItemCard 
          {item} 
          onclick={() => handleItemClick(item)} 
        />
      {/each}
    </div>
  {/if}
</section>

<!-- Item Modal -->
{#if showModal && selectedItem}
  <ItemModal 
    item={selectedItem} 
    onClose={handleCloseModal} 
  />
{/if}

<style>
  .catalog {
    padding: var(--spacing-lg) 0;
  }

  .catalog-header {
    text-align: center;
    margin-bottom: var(--spacing-xl);
  }

  .catalog-title {
    color: var(--color-primary);
    margin-bottom: var(--spacing-sm);
  }

  .catalog-subtitle {
    color: var(--color-text-secondary);
    font-size: var(--font-size-lg);
  }

  .catalog-controls {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-xl);
  }

  .search-box {
    position: relative;
    max-width: 400px;
    margin: 0 auto;
    width: 100%;
  }

  .search-icon {
    position: absolute;
    left: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
  }

  .search-input {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 2.75rem;
    background-color: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-full);
    color: var(--color-text);
    font-size: var(--font-size-base);
    transition: all var(--transition-fast);
  }

  .search-input:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-alpha);
  }

  .search-input::placeholder {
    color: var(--color-text-muted);
  }

  .category-filters {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }

  .category-btn {
    padding: var(--spacing-xs) var(--spacing-md);
    background-color: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-full);
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
    font-weight: 500;
    transition: all var(--transition-fast);
  }

  .category-btn:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .category-btn.active {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }

  .items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--spacing-lg);
  }

  .loading-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--spacing-lg);
  }

  .skeleton-card {
    background-color: var(--color-surface);
    border-radius: var(--border-radius-lg);
    overflow: hidden;
  }

  .skeleton-image {
    width: 100%;
    height: 200px;
  }

  .skeleton-content {
    padding: var(--spacing-md);
  }

  .skeleton-title {
    height: 1.25rem;
    width: 80%;
    margin-bottom: var(--spacing-sm);
  }

  .skeleton-price {
    height: 1rem;
    width: 40%;
  }

  .catalog-error,
  .catalog-empty {
    text-align: center;
    padding: var(--spacing-2xl);
    color: var(--color-text-secondary);
  }

  .error-icon,
  .empty-icon {
    font-size: 4rem;
    margin-bottom: var(--spacing-md);
  }

  .error-message {
    font-size: var(--font-size-sm);
    color: var(--color-error);
    margin-bottom: var(--spacing-md);
  }

  @media (max-width: 768px) {
    .catalog-header {
      margin-bottom: var(--spacing-lg);
    }

    .items-grid,
    .loading-grid {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: var(--spacing-md);
    }
  }
</style>
